/* eslint-disable react-refresh/only-export-components */
// ======================================================================================|
// A7Y-1898 — Shared affiliate-admin UI helpers: state→Badge colour maps, a
// cents→currency formatter, the self-referral evidence renderer, and a few
// small presentational primitives reused across the affiliate pages.

import { Badge, Text } from "@a7y/tailwind-catalyst";
import type { ReactNode } from "react";
import type {
  AffiliateStatus,
  CommissionState,
  PayoutState,
  ReferralConversionSignals,
  ReferralState,
} from "./types";

type PayoutOnboardingState =
  | "not_started"
  | "pending"
  | "active"
  | "restricted"
  | "unsupported_country";

// --------------------------------------------------------------------------------------
// State → Badge colour maps (match the org-detail palette conventions).

export const AFFILIATE_STATUS_COLORS: Record<AffiliateStatus, string> = {
  pending: "amber",
  active: "green",
  disabled: "zinc",
  suspicious: "red",
};

export const COMMISSION_STATE_COLORS: Record<CommissionState, string> = {
  pending: "amber",
  due: "blue",
  paid: "green",
  voided: "zinc",
  clawback: "red",
  reconciled: "zinc",
  written_off: "red",
};

export const PAYOUT_STATE_COLORS: Record<PayoutState, string> = {
  scheduled: "amber",
  processing: "blue",
  paid: "green",
  failed: "red",
};

export const REFERRAL_STATE_COLORS: Record<ReferralState, string> = {
  visitor: "zinc",
  lead: "blue",
  conversion: "green",
};

export const ONBOARDING_STATE_COLORS: Record<PayoutOnboardingState, string> = {
  not_started: "zinc",
  pending: "amber",
  active: "green",
  restricted: "red",
  unsupported_country: "red",
};

export function AffiliateStatusBadge({ status }: { status: AffiliateStatus }) {
  return <Badge color={AFFILIATE_STATUS_COLORS[status] ?? "zinc"}>{status.toUpperCase()}</Badge>;
}

export function CommissionStateBadge({ state }: { state: CommissionState }) {
  return <Badge color={COMMISSION_STATE_COLORS[state] ?? "zinc"}>{state.toUpperCase()}</Badge>;
}

export function PayoutStateBadge({ state }: { state: PayoutState }) {
  return <Badge color={PAYOUT_STATE_COLORS[state] ?? "zinc"}>{state.toUpperCase()}</Badge>;
}

export function ReferralStateBadge({ state }: { state: ReferralState }) {
  return <Badge color={REFERRAL_STATE_COLORS[state] ?? "zinc"}>{state.toUpperCase()}</Badge>;
}

export function OnboardingStateBadge({ state }: { state: PayoutOnboardingState }) {
  return (
    <Badge color={ONBOARDING_STATE_COLORS[state] ?? "zinc"}>
      {state.replace(/_/g, " ").toUpperCase()}
    </Badge>
  );
}

// --------------------------------------------------------------------------------------
// Money. Commission/payout amounts are stored in minor units (cents).

/** Format a minor-unit (cents) amount in its currency, e.g. 12345 → "$123.45". */
export function formatCents(amountCents: number, currency: string = "usd"): string {
  const value = (amountCents ?? 0) / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Unknown/unsupported currency code — fall back to a plain number + code.
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

// --------------------------------------------------------------------------------------
// Self-referral evidence (A7Y-1896 conversionSignals) → labelled rows.

export interface EvidenceRow {
  label: string;
  /** true = a fraud signal fired; false = checked, clean; null = not evaluated. */
  matched: boolean | null;
  detail?: string;
}

/** Turn a referral's conversion signals into displayable evidence rows. */
export function buildEvidenceRows(
  signals: ReferralConversionSignals | null | undefined,
): EvidenceRow[] {
  if (!signals) return [];
  return [
    { label: "Owner email matches affiliate", matched: signals.ownerEmailMatchesAffiliate },
    {
      label: "Card fingerprint shared with affiliate",
      matched: signals.cardFingerprintShared,
      detail: signals.cardFingerprint ? `fp: ${signals.cardFingerprint}` : undefined,
    },
    { label: "Signup IP hash match", matched: signals.ipHashMatch },
    { label: "Device id match", matched: signals.deviceIdMatch },
  ];
}

function evidenceColor(matched: boolean | null): string {
  if (matched === true) return "red";
  if (matched === false) return "green";
  return "zinc";
}

function evidenceLabel(matched: boolean | null): string {
  if (matched === true) return "MATCH";
  if (matched === false) return "CLEAN";
  return "N/A";
}

/** Render conversion-signal evidence as a compact list of labelled badges. */
export function SignalEvidence({
  signals,
}: {
  signals: ReferralConversionSignals | null | undefined;
}) {
  const rows = buildEvidenceRows(signals);
  if (rows.length === 0) {
    return <Text className="text-xs text-zinc-500">No conversion signals captured</Text>;
  }
  return (
    <div className="flex flex-col gap-1">
      {rows.map((row) => (
        <div key={row.label} className="flex items-center gap-2 text-xs">
          <Badge color={evidenceColor(row.matched)}>{evidenceLabel(row.matched)}</Badge>
          <span className="text-zinc-700 dark:text-zinc-300">{row.label}</span>
          {row.detail && (
            <span className="font-mono text-[11px] text-zinc-400">{row.detail}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------------------
// Small presentational primitives.

/** A titled card shell, matching org-detail's `Section`. */
export function Section({
  title,
  icon,
  actions,
  children,
}: {
  title: string;
  icon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {icon && <span className="text-zinc-950 dark:text-white">{icon}</span>}
          <span className="text-base font-semibold text-zinc-950 dark:text-white">{title}</span>
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

/** Centered spinner for a full-panel loading state. */
export function LoadingPanel() {
  return (
    <div className="flex h-40 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
    </div>
  );
}

/** Headline stat card for the program overview. */
export function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-5 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <Text className="text-sm text-zinc-500 dark:text-zinc-400">{label}</Text>
      <div className="mt-1 text-2xl font-semibold text-zinc-950 dark:text-white">{value}</div>
      {sub != null && <div className="mt-1 text-xs text-zinc-400">{sub}</div>}
    </div>
  );
}
